import React from 'react';
import { X, Scan, Plus, Minus } from 'lucide-react';

// Fallback defaults – overridden by props when available
// Fallback defaults – overridden by props when available
const DEFAULT_CANVAS_W = 2000;
const DEFAULT_CANVAS_H = 1400;

/* ═══════════════════════════════════════════════
 *  MiniMap – Graph mini navigation map
 * ═══════════════════════════════════════════════ */
class MiniMap extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isDragging: false };
        this.containerRef = React.createRef();
    }

    handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: true });
        this.updateScrollFromEvent(e);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e) => {
        if (!this.state.isDragging) return;
        this.updateScrollFromEvent(e);
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    updateScrollFromEvent = (e) => {
        const el = this.containerRef.current;
        if (!el || !this.props.onPanTo) return;
        const rect = el.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const pctX = Math.max(0, Math.min(1, clickX / rect.width));
        const pctY = Math.max(0, Math.min(1, clickY / rect.height));
        this.props.onPanTo(pctX, pctY);
    };

    render() {
        const {
            cards = [],
            posMap = {},
            canvasW,
            canvasH,
            scrollLeft,
            scrollTop,
            stageW,
            stageH,
            zoom,
            marginLeft,
            marginTop
        } = this.props;

        if (!canvasW || !canvasH || !stageW || !stageH) return null;

        const MINI_W = 200;
        const MINI_H = 125;
        const PADDING_X = 16;
        const PADDING_Y = 14;

        const availW = MINI_W - PADDING_X * 2;
        const availH = MINI_H - PADDING_Y * 2;

        // Calculate exact bounding box across all cards and canvas dimensions
        let minX = 0;
        let maxX = canvasW;
        let minY = 0;
        let maxY = canvasH;

        if (cards.length > 0) {
            let cMinX = Infinity, cMaxX = -Infinity, cMinY = Infinity, cMaxY = -Infinity;
            let validCardsCount = 0;

            cards.forEach((card) => {
                const p = posMap[card.id];
                if (p) {
                    validCardsCount++;
                    if (p.x < cMinX) cMinX = p.x;
                    if (p.x + p.w > cMaxX) cMaxX = p.x + p.w;
                    if (p.y < cMinY) cMinY = p.y;
                    if (p.y + p.h > cMaxY) cMaxY = p.y + p.h;
                }
            });

            if (validCardsCount > 0) {
                minX = Math.min(0, cMinX);
                maxX = Math.max(canvasW, cMaxX);
                minY = Math.min(0, cMinY);
                maxY = Math.max(canvasH, cMaxY);
            }
        }

        const graphW = Math.max(100, maxX - minX);
        const graphH = Math.max(100, maxY - minY);

        const scaleX = availW / graphW;
        const scaleY = availH / graphH;
        const scale = Math.min(scaleX, scaleY);

        const contentW = graphW * scale;
        const contentH = graphH * scale;

        const offsetX = (MINI_W - contentW) / 2;
        const offsetY = (MINI_H - contentH) / 2;

        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const totalScaledW = canvasW * validZoom;
        const totalScaledH = canvasH * validZoom;

        const visibleRatioW = Math.min(1, stageW / totalScaledW);
        const visibleRatioH = Math.min(1, stageH / totalScaledH);

        const mapCanvasW = canvasW * scale;
        const mapCanvasH = canvasH * scale;

        // Viewport indicator dimensions with +4px padding spacing for breathing room
        const VP_PAD = 4;
        const rawVpW = Math.max(16, mapCanvasW * visibleRatioW);
        const rawVpH = Math.max(12, mapCanvasH * visibleRatioH);

        const maxScrollX = Math.max(1, totalScaledW - stageW);
        const maxScrollY = Math.max(1, totalScaledH - stageH);

        const scrollRatioX = Math.max(0, Math.min(1, (scrollLeft - marginLeft) / maxScrollX));
        const scrollRatioY = Math.max(0, Math.min(1, (scrollTop - marginTop) / maxScrollY));

        const rawVpX = offsetX + Math.max(0, Math.min(mapCanvasW - rawVpW, scrollRatioX * (mapCanvasW - rawVpW)));
        const rawVpY = offsetY + Math.max(0, Math.min(mapCanvasH - rawVpH, scrollRatioY * (mapCanvasH - rawVpH)));

        // Inset/expand viewport indicator by VP_PAD to provide comfortable spacing around nodes
        const vpX = Math.max(2, rawVpX - VP_PAD);
        const vpY = Math.max(2, rawVpY - VP_PAD);
        const vpW = Math.min(MINI_W - vpX - 2, rawVpW + VP_PAD * 2);
        const vpH = Math.min(MINI_H - vpY - 2, rawVpH + VP_PAD * 2);

        // Pre-calculate mapped position and dimension for all valid cards
        const miniNodes = [];
        cards.forEach((card) => {
            const p = posMap[card.id];
            if (!p) return;

            const baseNx = offsetX + (p.x - minX) * scale;
            const baseNy = offsetY + (p.y - minY) * scale;
            const baseNw = p.w * scale;
            const baseNh = p.h * scale;

            miniNodes.push({
                card,
                p,
                nx: baseNx,
                ny: baseNy,
                nw: Math.max(4, baseNw),
                nh: Math.max(4, baseNh)
            });
        });

        // Strict 1px gap enforcement between adjacent cards
        const GAP = 1.0;
        miniNodes.forEach((node) => {
            miniNodes.forEach((other) => {
                if (node === other) return;
                // Horizontal gap enforcement
                if (other.nx > node.nx && Math.abs(other.ny - node.ny) < node.nh * 0.85) {
                    const maxAllowedW = other.nx - node.nx - GAP;
                    if (node.nw > maxAllowedW) {
                        node.nw = Math.max(3, maxAllowedW);
                    }
                }
                // Vertical gap enforcement
                if (other.ny > node.ny && Math.abs(other.nx - node.nx) < node.nw * 0.85) {
                    const maxAllowedH = other.ny - node.ny - GAP;
                    if (node.nh > maxAllowedH) {
                        node.nh = Math.max(3, maxAllowedH);
                    }
                }
            });
        });

        return (
            <div
                className="qd-data-lineage-minimap lin-minimap"
                ref={this.containerRef}
                onMouseDown={this.handleMouseDown}
                title="MiniMap – Click or drag to navigate graph"
            >
                <div
                    className="qd-data-lineage-minimap__inner lin-minimap-inner"
                    style={{ width: `${MINI_W}px`, height: `${MINI_H}px` }}
                >
                    {/* Mini nodes */}
                    {miniNodes.map(({ card, nx, ny, nw, nh }) => {
                        const isCritical = card.status === 'critical' || card.isCritical;
                        const isSource = card.type === 'source' || card.isSource;

                        // Color codes matched directly to graph legend & screenshot:
                        // Critical / Affected (Red): #F64E60 | Border: #D83B50
                        // Source (Blue): #007BFF | Border: #0056B3
                        // Regular Gray: #94A3B8 | Border: #64748B
                        let color = 'var(--qd-minimap-node-regular)';

                        if (isCritical) {
                            color = 'var(--qd-minimap-node-critical)';
                        } else if (isSource) {
                            color = 'var(--qd-minimap-node-source)';
                        }

                        return (
                            <div
                                key={card.id}
                                className={`qd-data-lineage-minimap__node lin-mini-node ${isCritical ? 'is-critical qd-data-lineage-minimap__node--critical' : ''} ${isSource ? 'is-source qd-data-lineage-minimap__node--source' : ''}`}
                                style={{
                                    left: `${Math.round(nx * 10) / 10}px`,
                                    top: `${Math.round(ny * 10) / 10}px`,
                                    width: `${Math.max(3, Math.round(nw * 10) / 10)}px`,
                                    height: `${Math.max(3, Math.round(nh * 10) / 10)}px`,
                                    backgroundColor: color,
                                    borderColor: 'transparent'
                                }}
                            />
                        );
                    })}

                    {/* Viewport box */}
                    <div
                        className="qd-data-lineage-minimap__vp lin-minimap-vp"
                        style={{
                            left: `${vpX}px`,
                            top: `${vpY}px`,
                            width: `${vpW}px`,
                            height: `${vpH}px`
                        }}
                    />
                </div>
            </div>
        );
    }
}

/**
 * Shared pan/zoom/theme chrome for LineageModal and WorkflowModal.
 * Pass header + canvas body via props; shell owns interaction state.
 *
 * New props:
 *  - canvasW / canvasH: dynamic canvas dimensions
 *  - autoFit: if true, automatically fit-to-screen on first render
 */
export default class GraphCanvasShell extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            zoom: 1.0,
            marginLeft: 24,
            marginTop: 24,
            scrollLeft: 0,
            scrollTop: 0,
            stageW: 0,
            stageH: 0,
            ready: false,
            theme: props.defaultTheme || 'light'
        };
        this.stageRef = React.createRef();
        this.panActive = false;
        this.panStartX = 0;
        this.panStartY = 0;
        this.panStartScrollLeft = 0;
        this.panStartScrollTop = 0;
        this.resizeObserver = null;
        this.resizeRaf = null;
        this.scrollRaf = null;
        this.didAutoFit = false;
    }

    get cw() { return this.props.canvasW || DEFAULT_CANVAS_W; }
    get ch() { return this.props.canvasH || DEFAULT_CANVAS_H; }

    componentDidMount() {
        if (this.props.show) {
            this.openShell();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.show !== this.props.show) {
            if (this.props.show) {
                this.didAutoFit = false;
                this.openShell();
            } else {
                this.closeShell();
            }
            return;
        }

        // NB: `children` is a fresh element on every parent render, so comparing it
        // by identity matched constantly and re-centered the scroll on any state
        // change (expanding a card, tracing, opening the panel). Only a real content
        // swap re-centers; a size change just refreshes the centering margins.
        const contentChanged = prevProps.contentKey !== this.props.contentKey;
        const sizeChanged = prevProps.canvasW !== this.props.canvasW
            || prevProps.canvasH !== this.props.canvasH;

        if (this.props.show && (contentChanged || sizeChanged)) {
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
                this.updateLayout({ centerScroll: contentChanged });
                if (!this.state.ready) this.setState({ ready: true });
            });
        }
    }

    componentWillUnmount() {
        this.closeShell();
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }

    openShell = () => {
        document.body.style.overflow = 'hidden';
        this.observeStage();
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
            const stage = this.stageRef.current;
            let nextZoom = this.state.zoom;
            if (this.props.autoFit && !this.didAutoFit && stage && stage.clientWidth > 0 && stage.clientHeight > 0) {
                this.didAutoFit = true;
                const fitW = (stage.clientWidth - 96) / this.cw;
                const fitH = (stage.clientHeight - 72) / this.ch;
                nextZoom = Math.min(1.2, Math.max(0.15, Math.min(fitW, fitH)));
            }
            const visualW = this.cw * nextZoom;
            const visualH = this.ch * nextZoom;
            let marginLeft = 24;
            let marginTop = 24;
            if (stage && stage.clientWidth > 0) {
                marginLeft = Math.max(24, Math.round((stage.clientWidth - visualW) / 2));
            }
            if (stage && stage.clientHeight > 0) {
                marginTop = Math.max(24, Math.round((stage.clientHeight - visualH) / 2));
            }

            this.setState({ zoom: nextZoom, marginLeft, marginTop, ready: true }, () => {
                if (stage) {
                    stage.scrollLeft = Math.max(0, (stage.scrollWidth - stage.clientWidth) / 2);
                    stage.scrollTop = Math.max(0, (stage.scrollHeight - stage.clientHeight) / 2);
                }
            });
        });
    };

    closeShell = () => {
        document.body.style.overflow = '';
        this.unobserveStage();
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.setState({ ready: false });
    };

    observeStage = () => {
        this.unobserveStage();
        const stage = this.stageRef.current;
        if (!stage) return;
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
                this.resizeRaf = requestAnimationFrame(() => {
                    this.updateLayout({ centerScroll: false });
                });
            });
            this.resizeObserver.observe(stage);
        }
        stage.addEventListener('wheel', this.handleWheel, { passive: false });
    };

    unobserveStage = () => {
        const stage = this.stageRef.current;
        if (stage) {
            stage.removeEventListener('wheel', this.handleWheel);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.resizeRaf) {
            cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = null;
        }
    };

    handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            const now = Date.now();
            if (this.lastWheelTime && now - this.lastWheelTime < 100) {
                return;
            }
            this.lastWheelTime = now;

            if (e.deltaY < 0) {
                this.handleZoom(1);
            } else if (e.deltaY > 0) {
                this.handleZoom(-1);
            }
        }
    };

    updateLayout = ({ centerScroll = false } = {}) => {
        const stage = this.stageRef.current;
        if (!stage) return;

        const { zoom, marginLeft: prevMarginLeft = 24, marginTop: prevMarginTop = 24 } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const visualW = this.cw * validZoom;
        const visualH = this.ch * validZoom;

        let marginLeft = 24;
        let marginTop = 24;

        if (stage.clientWidth > 0) {
            marginLeft = Math.max(24, Math.round((stage.clientWidth - visualW) / 2));
        }
        if (stage.clientHeight > 0) {
            marginTop = Math.max(24, Math.round((stage.clientHeight - visualH) / 2));
        }

        const deltaX = marginLeft - prevMarginLeft;
        const deltaY = marginTop - prevMarginTop;

        const marginChanged =
            Math.abs(deltaX) >= 2
            || Math.abs(deltaY) >= 2;

        if (marginChanged && !centerScroll) {
            stage.scrollLeft = Math.max(0, stage.scrollLeft + deltaX);
            stage.scrollTop = Math.max(0, stage.scrollTop + deltaY);
        }

        const applyScroll = () => {
            if (!centerScroll || !this.stageRef.current) return;
            const el = this.stageRef.current;
            el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
            el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
        };

        if (marginChanged) {
            this.setState({
                marginLeft,
                marginTop,
                scrollLeft: stage.scrollLeft,
                scrollTop: stage.scrollTop,
                stageW: stage.clientWidth,
                stageH: stage.clientHeight
            }, applyScroll);
        } else {
            this.setState({
                scrollLeft: stage.scrollLeft,
                scrollTop: stage.scrollTop,
                stageW: stage.clientWidth,
                stageH: stage.clientHeight
            });
            applyScroll();
        }
    };

    handleStageScroll = () => {
        if (!this.stageRef.current) return;
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => {
            const stage = this.stageRef.current;
            if (!stage) return;
            this.setState({
                scrollLeft: stage.scrollLeft,
                scrollTop: stage.scrollTop,
                stageW: stage.clientWidth,
                stageH: stage.clientHeight
            });
        });
    };

    handleMiniMapPan = (pctX, pctY) => {
        const stage = this.stageRef.current;
        if (!stage) return;
        const { zoom, marginLeft, marginTop } = this.state;
        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const totalW = this.cw * validZoom;
        const totalH = this.ch * validZoom;

        const maxScrollX = Math.max(1, totalW - stage.clientWidth);
        const maxScrollY = Math.max(1, totalH - stage.clientHeight);

        stage.scrollLeft = marginLeft + pctX * maxScrollX;
        stage.scrollTop = marginTop + pctY * maxScrollY;

        this.setState({
            scrollLeft: stage.scrollLeft,
            scrollTop: stage.scrollTop
        });
    };

    handleMouseDown = (e) => {
        if (e.target.closest('.qd-data-lineage-zoom') || e.target.closest('.lin-zoom') || e.target.closest('.lin-legend') || e.target.closest('.qd-data-lineage-minimap') || e.target.closest('.lin-minimap')) return;
        this.panActive = true;
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
        this.panStartScrollLeft = this.stageRef.current ? this.stageRef.current.scrollLeft : 0;
        this.panStartScrollTop = this.stageRef.current ? this.stageRef.current.scrollTop : 0;
        if (this.stageRef.current) this.stageRef.current.classList.add('grabbing');
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e) => {
        if (!this.panActive || !this.stageRef.current) return;
        this.stageRef.current.scrollLeft = this.panStartScrollLeft - (e.clientX - this.panStartX);
        this.stageRef.current.scrollTop = this.panStartScrollTop - (e.clientY - this.panStartY);
    };

    handleMouseUp = () => {
        this.panActive = false;
        if (this.stageRef.current) this.stageRef.current.classList.remove('grabbing');
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    };

    handleZoom = (dir) => {
        this.setState((prev) => {
            let nextZoom = prev.zoom;
            const stage = this.stageRef.current;
            if (dir === 'fit') {
                if (stage && stage.clientWidth > 0 && stage.clientHeight > 0) {
                    const fitW = (stage.clientWidth - 96) / this.cw;
                    const fitH = (stage.clientHeight - 72) / this.ch;
                    nextZoom = Math.min(1.2, Math.max(0.15, Math.min(fitW, fitH)));
                } else {
                    nextZoom = 1.0;
                }
            } else {
                const step = typeof dir === 'number' ? dir : 0;
                nextZoom = Math.min(1.6, Math.max(0.15, prev.zoom + step * 0.1));
            }

            if (isNaN(nextZoom) || nextZoom <= 0) {
                nextZoom = 1.0;
            }
            return { zoom: nextZoom };
        }, () => {
            this.updateLayout({ centerScroll: true });
        });
    };

    render() {
        if (!this.props.show) return null;

        const { zoom, marginLeft, marginTop, scrollLeft, scrollTop, stageW, stageH, theme, ready } = this.state;
        const {
            onClose,
            title,
            stats,
            headerExtra = null,
            legend,
            sidePanel = null,
            cards = [],
            posMap = {},
            children
        } = this.props;

        const validZoom = isNaN(zoom) || zoom <= 0 ? 1.0 : zoom;
        const layoutShrinkX = this.cw * (validZoom - 1);
        const layoutShrinkY = this.ch * (validZoom - 1);

        return (
            <div className="qd-data-lineage-overlay lin-overlay on" id="linOverlay">
                <div className={`qd-data-lineage-modal lin-modal ${theme === 'dark' ? 'dark-theme' : ''}`}>
                    <div className="qd-data-lineage-header lin-head">
                        <div>
                            <div className="qd-data-lineage-header__title lin-h-title">{title}</div>
                            {stats}
                        </div>
                        <div className="qd-data-lineage-header__actions lin-h-r">
                            {headerExtra}
                            <span className="qd-data-lineage-header__icon lin-hicon"></span>
                            <button className="qd-data-lineage-header__close lin-close" onClick={onClose}><X size={18} /></button>
                        </div>
                    </div>

                    <div className="qd-data-lineage-viewport lin-viewport">
                        {/* Wrapper keeps the zoom controls + legend anchored to the graph
                            area, so an open side panel sits beside them instead of under */}
                        <div className="lin-stage-wrap">
                            <div
                                className="lin-stage"
                                id="linStage"
                                ref={this.stageRef}
                                onMouseDown={this.handleMouseDown}
                                onScroll={this.handleStageScroll}
                            >
                                <div
                                    className="lin-canvas"
                                    id="linCanvas"
                                    style={{
                                        transform: `scale(${validZoom})`,
                                        width: `${this.cw}px`,
                                        height: `${this.ch}px`,
                                        marginLeft: `${marginLeft}px`,
                                        marginTop: `${marginTop}px`,
                                        marginRight: `${24 + layoutShrinkX}px`,
                                        marginBottom: `${24 + layoutShrinkY}px`,
                                        opacity: ready ? 1 : 0,
                                        transition: 'opacity 0.15s ease'
                                    }}
                                >
                                    {children}
                                </div>
                            </div>

                            <MiniMap
                                cards={cards}
                                posMap={posMap}
                                canvasW={this.cw}
                                canvasH={this.ch}
                                scrollLeft={scrollLeft}
                                scrollTop={scrollTop}
                                stageW={stageW}
                                stageH={stageH}
                                zoom={validZoom}
                                marginLeft={marginLeft}
                                marginTop={marginTop}
                                onPanTo={this.handleMiniMapPan}
                            />

                            <div className="lin-zoom">
                                <button onClick={() => this.handleZoom('fit')} title="Fit to screen"><Scan size={17} /></button>
                                <button onClick={() => this.handleZoom(1)}><Plus size={17} /></button>
                                <span className="pct">{Math.round(validZoom * 100)}%</span>
                                <button onClick={() => this.handleZoom(-1)}><Minus size={17} /></button>
                            </div>

                            {legend}
                        </div>

                        {sidePanel}
                    </div>
                </div>
            </div>
        );
    }
}

